import { MoreHorizontal } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// «…»-meny for sjeldne handlinger i sidehodet (eksport, import, BRREG,
// arkiverte). Sidehodet får da én tydelig hovedknapp («Ny …») i stedet for
// fem like knapper med ramme.
//
// Hvorfor ikke Radix DropdownMenu/Popover: handlingene eier sine egne
// dialoger (import, arkiverte, bekreftelse). Radix avmonterer menyinnholdet
// når menyen lukkes, og da ville dialogen som nettopp ble åpnet forsvinne
// igjen. Her står innholdet montert og skjules bare, så dialogene lever
// videre etter at menyen er lukket.

type ActionsMenuContextValue = { close: () => void };

const ActionsMenuContext = createContext<ActionsMenuContextValue | null>(null);

export const ActionsMenu = ({
  children,
  label,
}: {
  children: ReactNode;
  label?: string;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Fokus på første valg når menyen åpnes, som i en vanlig meny.
    rootRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')
      ?.focus();
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([disabled])',
      ) ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === "ArrowDown"
        ? items[(index + 1) % items.length]
        : items[(index - 1 + items.length) % items.length];
    next?.focus();
  };

  const triggerLabel =
    label ?? translate("crm.actions.more", { _: "Flere handlinger" });

  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="icon"
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal />
      </Button>
      <div
        id={menuId}
        role="menu"
        aria-label={triggerLabel}
        hidden={!open}
        className="absolute right-0 top-full z-50 mt-1 flex min-w-52 flex-col rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 duration-150"
      >
        <ActionsMenuContext.Provider value={{ close }}>
          {children}
        </ActionsMenuContext.Provider>
      </div>
    </div>
  );
};

/**
 * Knapp for en handling i sidehodet. Utenfor en ActionsMenu er den en vanlig
 * knapp med ramme; inne i menyen blir den et menyvalg som lukker menyen når
 * den trykkes.
 */
export const ToolbarActionButton = ({
  className,
  onClick,
  ...props
}: ComponentProps<typeof Button>) => {
  const menu = useContext(ActionsMenuContext);
  if (!menu) {
    return (
      <Button
        variant="outline"
        className={cn("flex items-center gap-2", className)}
        onClick={onClick}
        {...props}
      />
    );
  }
  return (
    <Button
      variant="ghost"
      role="menuitem"
      className={cn(
        "h-8 w-full justify-start gap-2 px-2 font-normal",
        className,
      )}
      onClick={(event) => {
        menu.close();
        onClick?.(event);
      }}
      {...props}
    />
  );
};
